import React, { useCallback, useEffect, useRef } from "react";
import { useRebaseClient } from "@rebasepro/app";
import type { RebaseClient } from "@rebasepro/types";

import { callFunction, type SyncResult } from "./_email_shared";

/**
 * Reading the mailbox while somebody is looking at the panel.
 *
 * The cron is the backstop for the hours when nobody is; this is what makes a
 * reply appear about a minute after it arrives, rather than up to five minutes
 * after. It is the difference between an inbox you watch and one you remember
 * to refresh.
 *
 * ── Three throttles, because one is not enough ───────────────────────
 *
 * 1. **The tab must be visible.** A panel left open in a background tab for a
 *    weekend would otherwise poll all weekend. `visibilitychange` also fires the
 *    poll on *return*, which is the moment the answer is most wanted — coming
 *    back to the tab is exactly when you want to know what arrived.
 * 2. **One request in flight.** A slow IMAP session must not have a second
 *    request queued behind it and a third behind that; a stuck mailbox would
 *    turn into a pile-up.
 * 3. **The server floor** (`INBOX_MIN_POLL_SECONDS`). The only one that actually
 *    holds, and the reason the other two are conveniences rather than
 *    protections: this hook's rate is (tabs × operators), which no amount of
 *    client-side care can bound. Two tabs at 60 s each are two requests a
 *    minute; the server opens at most one IMAP session per floor regardless,
 *    and answers the rest from what it already has.
 *
 * The background poll never sets `force`. That is reserved for somebody pressing
 * the button, which is a person asking a question the cached answer does not
 * answer.
 */

/**
 * How often an open panel asks.
 *
 * A minute is under the threshold at which somebody would have reached for a
 * refresh button themselves, and it is deliberately *above* the server floor
 * (45 s) so a single operator is never throttled — the floor exists to bound
 * the many-tab case, not to fight the normal one.
 */
export const AUTO_SYNC_INTERVAL_MS = 60_000;

/** Fired on `window` when a poll actually stored something new. */
export const NEW_MAIL_EVENT = "sustentalent:new-mail";

export function useInboxAutoSync({
    enabled = true,
    intervalMs = AUTO_SYNC_INTERVAL_MS,
    onNewMail
}: {
    enabled?: boolean;
    intervalMs?: number;
    onNewMail?: (result: SyncResult) => void;
} = {}) {
    const client = useRebaseClient<RebaseClient>();
    const inFlight = useRef(false);
    // Held in a ref so changing the callback does not tear down the timer — a
    // parent that re-creates its handler every render would otherwise reset the
    // interval on every render and effectively never fire it.
    const handler = useRef(onNewMail);
    handler.current = onNewMail;

    const poll = useCallback(async () => {
        if (inFlight.current) return;
        if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
        inFlight.current = true;
        try {
            const result = await callFunction<SyncResult>(client, "sync-inbox", {});
            if (!result?.skipped && (result?.stored ?? 0) > 0) {
                handler.current?.(result);
                // Broadcast, so a screen that is *not* the one holding this hook
                // can still refresh. The alternative is a shared store for one
                // boolean, which is a lot of machinery for "something arrived".
                window.dispatchEvent(new CustomEvent(NEW_MAIL_EVENT, { detail: result }));
            }
        } catch {
            // Deliberately silent. This runs on a timer nobody asked for, and a
            // toast every sixty seconds because the mailbox is unreachable is
            // how a person learns to ignore the panel. The inbox screen already
            // shows the mailbox's own error banner, sourced from
            // `email_sync_state.last_error`, which is where this belongs.
        } finally {
            inFlight.current = false;
        }
    }, [client]);

    useEffect(() => {
        if (!enabled) return;

        // Not on mount: the panel has just loaded its threads from the database,
        // and an immediate poll would spend an IMAP session to tell it what it
        // already knows. The first tick is one interval away.
        const timer = setInterval(() => { void poll(); }, intervalMs);

        const onVisible = () => {
            if (document.visibilityState === "visible") void poll();
        };
        document.addEventListener("visibilitychange", onVisible);

        return () => {
            clearInterval(timer);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, [enabled, intervalMs, poll]);

    return { poll };
}

/**
 * The hook, mounted once, rendering nothing.
 *
 * Lives at the top of the admin so the mailbox is read whenever the panel is
 * open — not only while the "Correo" screen is on display. Somebody working
 * through the vacancy queue still wants the unread count to be true, and still
 * wants a reply to be waiting when they get to it.
 *
 * Gated on the admin role: `sync-inbox` is admin-only and would answer 401/403
 * to anyone else, once a minute, forever.
 */
export function InboxAutoSync({ enabled }: { enabled: boolean }) {
    useInboxAutoSync({ enabled });
    return null;
}
